/*
  Eco Natural Products - sold-out-last collection ordering.

  Reorders the paginated collection grid so that available products come before
  sold-out ones ACROSS the whole collection, not just within the current page.

  Key difference from the previous inline version: pages are fetched one at a
  time and the loop stops as soon as enough available products have been
  collected to fill the current page window. On page 1 of a collection that is
  mostly in stock this means 1 extra request instead of one per page.
*/
(() => {
  const init = () => {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    if (grid.dataset.soldOutLastGrid !== 'true') return;
    if (grid.dataset.soldOutLastInitialised === 'true') return;

    const sectionId = grid.dataset.id;
    const perPage = Number(grid.dataset.productsPerPage);
    const totalPages = Number(grid.dataset.totalPages);
    const currentPage = Number(grid.dataset.currentPage);

    if (!sectionId || !perPage || !totalPages || !currentPage) return;
    if (totalPages <= 1) return;

    grid.dataset.soldOutLastInitialised = 'true';

    const windowStart = (currentPage - 1) * perPage;
    const windowEnd = currentPage * perPage;

    const buildUrl = (page) => {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', sectionId);
      return url.toString();
    };

    const getProductKey = (item) => {
      const link = item.querySelector('a[href*="/products/"]');
      if (link) {
        try {
          return new URL(link.getAttribute('href'), window.location.origin).pathname;
        } catch (e) {
          return link.getAttribute('href');
        }
      }
      return item.textContent.trim().replace(/\s+/g, ' ').slice(0, 180);
    };

    const isSoldOut = (item) => {
      if (item.classList.contains('grid__item--sold-out')) return true;
      if (item.querySelector('.product-card-wrapper--sold-out')) return true;
      const disabledButton = item.querySelector('button[disabled]');
      if (!disabledButton) return false;
      const text = item.textContent.toLowerCase();
      return text.includes('sold out') || text.includes('out of stock');
    };

    const markSoldOut = (item, soldOut) => {
      item.classList.toggle('grid__item--sold-out', soldOut);
      const wrapper = item.querySelector('.card-wrapper, .product-card-wrapper');
      if (wrapper) wrapper.classList.toggle('product-card-wrapper--sold-out', soldOut);
    };

    const fetchPage = async (page) => {
      if (page === currentPage) return Array.from(grid.children);

      const response = await fetch(buildUrl(page), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error('Could not load collection page ' + page);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fetchedGrid = doc.getElementById('product-grid');
      if (!fetchedGrid) return [];
      return Array.from(fetchedGrid.children).map((item) => item.cloneNode(true));
    };

    const run = async () => {
      const seen = new Set();
      const available = [];
      const soldOut = [];
      let pagesFetched = 0;

      try {
        for (let page = 1; page <= totalPages; page += 1) {
          const items = await fetchPage(page);
          pagesFetched += 1;

          items.forEach((item) => {
            const key = getProductKey(item);
            if (!key || seen.has(key)) return;
            seen.add(key);

            const soldOutItem = isSoldOut(item);
            markSoldOut(item, soldOutItem);
            (soldOutItem ? soldOut : available).push(item);
          });

          // Early exit: the current page window is already fully covered by
          // available products, so nothing further can change it.
          if (available.length >= windowEnd) break;
        }

        const sorted = available.concat(soldOut);
        const windowItems = sorted.slice(windowStart, windowEnd);
        if (!windowItems.length) return;

        // Skip the DOM swap when the order is already correct (avoids a flash).
        const currentKeys = Array.from(grid.children).map(getProductKey).join('|');
        const nextKeys = windowItems.map(getProductKey).join('|');
        if (currentKeys === nextKeys) return;

        grid.replaceChildren(...windowItems);
      } catch (error) {
        console.warn('[sold-out-last] ordering skipped:', error);
      } finally {
        grid.removeAttribute('aria-busy');
        grid.dataset.soldOutLastPagesFetched = String(pagesFetched);
      }
    };

    run();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-run after facet/sort updates replace the grid.
  document.addEventListener('shopify:section:load', init);
})();
